import {
  type AssignmentInput,
  EXTERNAL_PROFILES,
  type ExternalProfile,
  type Invite,
  type Member,
  type MemberClearance,
  MONEY_LEVELS,
  type MoneyLevel,
  type MoneyScopeInput,
  type Role,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Send, UserPlus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { HistoryButton } from '../../components/History';
import { ViewAsButton, ViewAsLogPanel } from '../../components/ViewAs';
import { ApiError, api } from '../../lib/api';
import { date, plural, relative } from '../../lib/format';
import { counterpartiesQuery } from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { AssignmentsEditor } from './AssignmentsEditor';
import { ExternalProfileFields } from './ExternalProfileFields';
import { InviteResult, mailLine, mailNeedsAttention } from './InviteResult';
import { MemberPasswordReset } from './MemberPasswordReset';
import { MoneyScopesEditor } from './MoneyScopesEditor';

export function useRoles() {
  return useQuery({ queryKey: ['admin', 'roles'], queryFn: () => api<Role[]>('/admin/roles') });
}

function RoleChips({ m }: { m: Member }) {
  if (m.isOwner) return <Badge tone="ink">Владелец, все права</Badge>;
  if (!m.roles.length) return <span className="text-[13px] text-ink-3">ролей нет</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {m.roles.map((r) => (
        <Badge key={r.id}>
          {r.roleName}
          {r.scopeName ? <span className="font-normal text-ink-3">{r.scopeName}</span> : null}
        </Badge>
      ))}
    </div>
  );
}

function InviteDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const roles = useRoles();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [position, setPosition] = useState('');
  const [kind, setKind] = useState<'staff' | 'external'>('staff');
  // Контрагент внешнего участника (ВК-02): с первого входа видит только своё
  const [counterpartyId, setCounterpartyId] = useState('');
  const [externalProfile, setExternalProfile] = useState<ExternalProfile | ''>('');
  const [externalProjectIds, setExternalProjectIds] = useState<string[]>([]);
  const counterparties = useQuery({ ...counterpartiesQuery(), enabled: kind === 'external' });
  const [moneyLevel, setMoneyLevel] = useState<MoneyLevel>('none');
  const moneyHidden =
    kind === 'external' && externalProfile !== 'supplier' && externalProfile !== 'subcontractor';
  const me = useMe();
  const [assignments, setAssignments] = useState<AssignmentInput[]>([]);
  const [moneyScopes, setMoneyScopes] = useState<MoneyScopeInput[]>([]);
  const [created, setCreated] = useState<Invite | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const mutation = useMutation({
    mutationFn: () =>
      api<Invite>('/admin/invites', {
        body: {
          email,
          fullName: fullName || undefined,
          position: position || undefined,
          kind,
          moneyLevel: moneyHidden ? 'none' : moneyLevel,
          moneyScopes: moneyHidden || !me.tenant?.isOwner ? [] : moneyScopes,
          assignments,
          counterpartyId: kind === 'external' && counterpartyId ? counterpartyId : undefined,
          externalProfile: kind === 'external' ? externalProfile || null : null,
          externalProjectIds: kind === 'external' ? externalProjectIds : [],
        },
      }),
    onSuccess: (inv) => {
      setCreated(inv);
      qc.invalidateQueries({ queryKey: ['admin', 'invites'] });
    },
    onError: (err) => err instanceof ApiError && setErrors(err.fieldErrors()),
  });

  function reset(v: boolean) {
    if (!v) {
      setEmail('');
      setFullName('');
      setPosition('');
      setKind('staff');
      setCounterpartyId('');
      setExternalProfile('');
      setExternalProjectIds([]);
      setMoneyLevel('none');
      setAssignments([]);
      setMoneyScopes([]);
      setCreated(null);
      setErrors({});
      mutation.reset();
    }
    onOpenChange(v);
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    mutation.mutate();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={reset}
      title={created ? 'Приглашение готово' : 'Пригласить в портал'}
      description={
        created
          ? undefined
          : 'Человек получит письмо со ссылкой и инструкцией по своим правам: разделы, первые шаги и что делать каждый день.'
      }
      width="lg"
      footer={
        created ? (
          <Button variant="primary" onClick={() => reset(false)}>
            Готово
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={() => reset(false)}>
              Отмена
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="invite-form"
              loading={mutation.isPending}
              disabled={
                !email ||
                !assignments.length ||
                (kind === 'external' && (!counterpartyId || !externalProfile || !externalProjectIds.length))
              }
            >
              Создать приглашение
            </Button>
          </>
        )
      }
    >
      {created ? (
        <InviteResult
          email={created.email}
          link={created.link}
          expiresAt={created.expiresAt}
          mail={created.mail}
          message={created.message}
          previewId={created.id}
        />
      ) : (
        <form id="invite-form" onSubmit={submit} className="flex flex-col gap-4" noValidate>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Почта" htmlFor="inv-email" error={errors.email}>
              <Input
                id="inv-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                invalid={!!errors.email}
              />
            </Field>
            <Field label="Имя и фамилия" htmlFor="inv-name" hint="Можно оставить пустым">
              <Input id="inv-name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </Field>
            <Field label="Должность" htmlFor="inv-pos">
              <Input id="inv-pos" value={position} onChange={(e) => setPosition(e.target.value)} />
            </Field>
            <Field label="Кто это" htmlFor="inv-kind">
              <Select
                id="inv-kind"
                value={kind}
                onChange={(e) => setKind(e.target.value as 'staff' | 'external')}
              >
                <option value="staff">Сотрудник компании</option>
                <option value="external">Внешний участник: поставщик, подрядчик, заказчик</option>
              </Select>
            </Field>
            {kind === 'external' ? (
              <ExternalProfileFields
                prefix="inv"
                profile={externalProfile}
                onProfile={setExternalProfile}
                projectIds={externalProjectIds}
                onProjects={setExternalProjectIds}
              />
            ) : null}
            {kind === 'external' ? (
              <Field
                label="Организация участника"
                htmlFor="inv-cp"
                hint="Выберите реальную организацию участника из справочника контрагентов"
              >
                <Select
                  id="inv-cp"
                  value={counterpartyId}
                  onChange={(e) => setCounterpartyId(e.target.value)}
                >
                  <option value="">— выберите контрагента —</option>
                  {(counterparties.data ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
            {me.tenant?.isOwner ? (
              <Field label="Доступ к деньгам" htmlFor="inv-money" hint="Выдаётся человеку, а не роли (ДС-04)">
                <Select
                  id="inv-money"
                  value={moneyHidden ? 'none' : moneyLevel}
                  disabled={moneyHidden}
                  onChange={(e) => setMoneyLevel(e.target.value as MoneyLevel)}
                >
                  {Object.entries(MONEY_LEVELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
          </div>
          <Field label="Роли" error={errors.assignments}>
            {roles.data ? (
              <AssignmentsEditor roles={roles.data} value={assignments} onChange={setAssignments} />
            ) : (
              <Spinner />
            )}
          </Field>
          {me.tenant?.isOwner && !moneyHidden ? (
            <Field
              label="Деньги по проектам"
              hint="Сверх общего уровня: в этих проектах, направлениях и юрлицах человек видит суммы этого уровня"
            >
              <MoneyScopesEditor
                value={moneyScopes}
                onChange={setMoneyScopes}
                projectIds={kind === 'external' ? externalProjectIds : undefined}
              />
            </Field>
          ) : null}
          <ErrorBox error={Object.keys(errors).length ? null : mutation.error} />
        </form>
      )}
    </Dialog>
  );
}

function EditMemberDialog({ member, onClose }: { member: Member | null; onClose: () => void }) {
  const qc = useQueryClient();
  const roles = useRoles();
  const me = useMe();
  const [position, setPosition] = useState(member?.position ?? '');
  const [status, setStatus] = useState(member?.status ?? 'active');
  const [kind, setKind] = useState(member?.kind ?? 'staff');
  // Контрагент внешнего участника (ВК-02): подрядчик и поставщик видят только своё
  const [counterpartyId, setCounterpartyId] = useState<string>(member?.counterpartyId ?? '');
  const [externalProfile, setExternalProfile] = useState<ExternalProfile | ''>(member?.externalProfile ?? '');
  const [externalProjectIds, setExternalProjectIds] = useState<string[]>(member?.externalProjectIds ?? []);
  const counterparties = useQuery({ ...counterpartiesQuery(), enabled: kind === 'external' });
  const [moneyLevel, setMoneyLevel] = useState<MoneyLevel>(member?.moneyLevel ?? 'none');
  const moneyHidden =
    kind === 'external' && externalProfile !== 'supplier' && externalProfile !== 'subcontractor';
  const [assignments, setAssignments] = useState<AssignmentInput[]>(
    member?.roles.map((r) => ({ roleId: r.roleId, scopeType: r.scopeType, scopeId: r.scopeId })) ?? [],
  );
  // Деньги по проектам (ДС-18): список приходит и уходит только у владельца компании
  const [moneyScopes, setMoneyScopes] = useState<MoneyScopeInput[]>(
    // Прежняя версия API поля не отдаёт (выкладка идёт по очереди) — тогда пустой список
    (member?.moneyScopes ?? []).map(({ scopeType, scopeId, moneyLevel }) => ({
      scopeType,
      scopeId,
      moneyLevel,
    })),
  );
  // Обходной лист (СК-33): отключение с долгом по складу — только после явного подтверждения
  const [clearance, setClearance] = useState<MemberClearance | null>(null);
  const mutation = useMutation({
    mutationFn: (forceDisable?: boolean) =>
      api<Member>(`/admin/members/${member!.membershipId}`, {
        method: 'PATCH',
        body: {
          position: position || null,
          status,
          kind,
          assignments,
          counterpartyId: kind === 'external' && counterpartyId ? counterpartyId : null,
          externalProfile: kind === 'external' ? externalProfile || null : null,
          externalProjectIds: kind === 'external' ? externalProjectIds : [],
          ...(forceDisable ? { forceDisable: true } : {}),
          ...(me.tenant?.isOwner && !member!.isOwner
            ? { moneyLevel: moneyHidden ? 'none' : moneyLevel, moneyScopes: moneyHidden ? [] : moneyScopes }
            : {}),
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      if (member?.userId === me.user.id) qc.invalidateQueries({ queryKey: ['me'] });
      toast('Сохранено');
      onClose();
    },
    onError: (e) => {
      const due = (
        e instanceof ApiError ? (e.details as { clearance?: MemberClearance } | undefined) : undefined
      )?.clearance;
      if (due) setClearance(due);
    },
  });

  if (!member) return null;
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={member.fullName}
      description={member.email}
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={
              kind === 'external' &&
              status === 'active' &&
              (!counterpartyId || !externalProfile || !externalProjectIds.length)
            }
            onClick={() => mutation.mutate(false)}
          >
            Сохранить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Должность" htmlFor="m-pos">
            <Input id="m-pos" value={position} onChange={(e) => setPosition(e.target.value)} />
          </Field>
          <Field label="Кто это" htmlFor="m-kind">
            <Select
              id="m-kind"
              disabled={member.isOwner}
              value={kind}
              onChange={(e) => setKind(e.target.value as 'staff' | 'external')}
            >
              <option value="staff">Сотрудник</option>
              <option value="external">Внешний участник</option>
            </Select>
          </Field>
          {me.tenant?.isOwner && !member.isOwner ? (
            <Field label="Доступ к деньгам" htmlFor="m-money">
              <Select
                id="m-money"
                value={moneyHidden ? 'none' : moneyLevel}
                disabled={moneyHidden}
                onChange={(e) => setMoneyLevel(e.target.value as MoneyLevel)}
              >
                {Object.entries(MONEY_LEVELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          <Field label="Доступ" htmlFor="m-status">
            <Select
              id="m-status"
              value={status}
              disabled={member.isOwner}
              onChange={(e) => setStatus(e.target.value as 'active' | 'disabled')}
            >
              <option value="active">Открыт</option>
              <option value="disabled">Отключён</option>
            </Select>
          </Field>
        </div>
        {member.isOwner ? (
          <p className="rounded-md bg-surface-2 px-3 py-2.5 text-[13px] text-ink-2">
            Владелец компании получает все права независимо от ролей. Роли ниже нужны, чтобы его видели
            согласующим и подписантом в маршрутах.
          </p>
        ) : null}
        {kind === 'external' ? (
          <ExternalProfileFields
            prefix="m"
            profile={externalProfile}
            onProfile={setExternalProfile}
            projectIds={externalProjectIds}
            onProjects={setExternalProjectIds}
          />
        ) : null}
        {kind === 'external' ? (
          <Field
            label="Организация участника"
            htmlFor="m-cp"
            hint="Для открытого доступа контрагент обязателен. Чтобы снять связь, сначала отключите доступ"
          >
            <Select id="m-cp" value={counterpartyId} onChange={(e) => setCounterpartyId(e.target.value)}>
              <option value="">— не привязан —</option>
              {/* Сохранённый контрагент показывается, даже если справочник не загрузился */}
              {member.counterpartyId &&
              !(counterparties.data ?? []).some((c) => c.id === member.counterpartyId) ? (
                <option value={member.counterpartyId}>
                  {member.counterpartyName ?? 'текущий контрагент'}
                </option>
              ) : null}
              {(counterparties.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field label="Роли">
          {roles.data ? (
            <AssignmentsEditor roles={roles.data} value={assignments} onChange={setAssignments} />
          ) : (
            <Spinner />
          )}
        </Field>
        {me.tenant?.isOwner && !member.isOwner && !moneyHidden ? (
          <Field
            label="Деньги по проектам"
            hint="Сверх общего уровня: в этих проектах, направлениях и юрлицах человек видит суммы этого уровня"
          >
            <MoneyScopesEditor
              value={moneyScopes}
              onChange={setMoneyScopes}
              projectIds={kind === 'external' ? externalProjectIds : undefined}
            />
          </Field>
        ) : null}
        {clearance ? (
          <div className="rounded-md border border-warning/40 bg-warning-soft px-4 py-3 text-[14px] text-ink">
            <p className="font-medium">Обходной лист не закрыт — за сотрудником числится по складу:</p>
            <ul className="mt-2 list-disc pl-5 text-[13px]">
              {clearance.lines.slice(0, 12).map((l, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: строки перечня могут совпадать (два одинаковых инструмента)
                <li key={`${i}-${l}`}>{l}</li>
              ))}
              {clearance.lines.length > 12 ? <li>… и ещё {clearance.lines.length - 12}</li> : null}
            </ul>
            <p className="mt-2 text-[13px] text-ink-2">
              Лучше сначала принять всё на склад (выдача и инструмент — кладовщик, СИЗ — карточка выдачи).
              Отключить с долгом можно: долг останется в учёте склада за этим человеком.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                variant="danger"
                size="md"
                loading={mutation.isPending}
                onClick={() => mutation.mutate(true)}
              >
                Отключить с долгом
              </Button>
              <Button variant="ghost" size="md" onClick={() => setClearance(null)}>
                Не отключать
              </Button>
            </div>
          </div>
        ) : (
          <ErrorBox error={mutation.error} />
        )}
        {/* Не может войти — ссылка для нового пароля на почту человека (ДС-19) */}
        <MemberPasswordReset member={member} />
      </div>
    </Dialog>
  );
}

/**
 * Отправить приглашение заново (БЗ-31): новая ссылка на 7 дней и письмо с инструкцией; прежняя ссылка перестаёт
 * работать. Приглашение с доступом к деньгам заново отправляет только владелец компании.
 */
export function ResendDialog({ invite, onClose }: { invite: Invite; onClose: () => void }) {
  const qc = useQueryClient();
  const resend = useMutation({
    mutationFn: () => api<Invite>(`/admin/invites/${invite.id}/resend`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'invites'] }),
  });
  const done = resend.data;
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={done ? 'Приглашение отправлено заново' : 'Отправить приглашение заново'}
      description={done ? undefined : (invite.fullName ?? invite.email)}
      width="lg"
      footer={
        done ? (
          <Button variant="primary" onClick={onClose}>
            Готово
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button
              variant="primary"
              icon={<Send className="size-4" />}
              loading={resend.isPending}
              onClick={() => resend.mutate()}
            >
              Отправить заново
            </Button>
          </>
        )
      }
    >
      {done ? (
        <InviteResult
          email={done.email}
          link={done.link}
          expiresAt={done.expiresAt}
          mail={done.mail}
          message={done.message}
          previewId={done.id}
        />
      ) : (
        <div className="flex flex-col gap-3 text-[14px] leading-6 text-ink">
          <p>
            {invite.email} получит новую ссылку и письмо с инструкцией по своим правам. Прежняя ссылка
            перестанет работать, новая действует 7 дней. Роли и доступ приглашения не меняются.
          </p>
          <ErrorBox error={resend.error} />
        </div>
      )}
    </Dialog>
  );
}

export function MembersPage() {
  const qc = useQueryClient();
  const me = useMe();
  const members = useQuery({
    queryKey: ['admin', 'members'],
    queryFn: () => api<Member[]>('/admin/members'),
  });
  const invites = useQuery({
    queryKey: ['admin', 'invites'],
    queryFn: () => api<Invite[]>('/admin/invites'),
  });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editing, setEditing] = useState<Member | null>(null);
  const [resending, setResending] = useState<Invite | null>(null);

  const revoke = useMutation({
    mutationFn: (id: string) => api(`/admin/invites/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'invites'] });
      toast('Приглашение отозвано');
    },
  });

  // Открытые приглашения и истёкшие за последний месяц: истёкшее отправляют заново с новой ссылкой
  const now = Date.now();
  const pending = (invites.data ?? []).filter(
    (i) => !i.acceptedAt && !i.revokedAt && new Date(i.expiresAt).getTime() > now - 30 * 86_400_000,
  );
  const waiting = pending.filter((i) => new Date(i.expiresAt).getTime() > now).length;
  // Приглашение с доступом к деньгам отправляет заново только владелец компании — как и создаёт его
  const canResend = (i: Invite) => i.moneyLevel === 'none' || Boolean(me.tenant?.isOwner);

  return (
    <div className="flex flex-col gap-6">
      <Panel
        title="Сотрудники"
        meta={members.data ? plural(members.data.length, 'человек', 'человека', 'человек') : undefined}
        actions={
          <Button
            variant="primary"
            size="sm"
            icon={<UserPlus className="size-4" />}
            onClick={() => setInviteOpen(true)}
          >
            Пригласить
          </Button>
        }
        bodyClassName="p-0"
      >
        {members.isLoading ? (
          <div className="px-5">
            <Spinner />
          </div>
        ) : members.error ? (
          <div className="p-5">
            <ErrorBox error={members.error} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[14px]">
              <thead className="hidden bg-surface-2 text-left text-[12px] text-ink-2 md:table-header-group">
                <tr>
                  <th className="px-5 py-2.5 font-medium">Сотрудник</th>
                  <th className="px-5 py-2.5 font-medium">Роли</th>
                  <th className="px-5 py-2.5 font-medium whitespace-nowrap">Последний вход</th>
                  <th className="px-5 py-2.5 font-medium">Доступ</th>
                  <th className="w-24 px-5 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {members.data?.map((m) => (
                  <tr
                    key={m.membershipId}
                    className="flex flex-col gap-2 border-t border-line px-5 py-3 md:table-row md:p-0"
                  >
                    <td className="md:px-5 md:py-3 md:align-top">
                      <Link
                        to="/admin/members/$memberId"
                        params={{ memberId: m.membershipId }}
                        className="inline-flex min-h-11 items-center font-medium text-ink underline-offset-2 hover:underline md:min-h-0"
                      >
                        {m.fullName}
                      </Link>
                      <div className="text-[13px] text-ink-2">
                        {m.email}
                        {m.position ? `, ${m.position}` : ''}
                        {m.kind === 'external'
                          ? `, ${m.externalProfile ? EXTERNAL_PROFILES[m.externalProfile] : 'профиль не задан'}`
                          : ''}
                      </div>
                      <div className="text-[12px] text-ink-3">
                        деньги: {MONEY_LEVELS[m.moneyLevel].toLowerCase()}
                        {m.moneyScopes?.length
                          ? `; по проектам: ${m.moneyScopes
                              .map(
                                (x) =>
                                  `${x.scopeName ?? 'область'} — ${MONEY_LEVELS[x.moneyLevel].toLowerCase()}`,
                              )
                              .join(', ')}`
                          : ''}
                      </div>
                    </td>
                    <td className="md:px-5 md:py-3 md:align-top">
                      <RoleChips m={m} />
                    </td>
                    <td className="text-[13px] whitespace-nowrap text-ink-2 md:px-5 md:py-3 md:align-top">
                      {relative(m.lastLoginAt)}
                    </td>
                    <td className="md:px-5 md:py-3 md:align-top">
                      {m.kind === 'external' &&
                      (!m.externalProfile || !m.counterpartyId || !m.externalProjectIds.length) ? (
                        <Badge tone="danger">Требуется настройка</Badge>
                      ) : m.status === 'active' ? (
                        <Badge tone="success">Открыт</Badge>
                      ) : (
                        <Badge tone="danger">Отключён</Badge>
                      )}
                    </td>
                    <td className="md:px-5 md:py-2 md:align-top">
                      <div className="flex items-center gap-1 md:justify-end">
                        <Link
                          to="/admin/members/$memberId"
                          params={{ memberId: m.membershipId }}
                          className="inline-flex h-11 items-center rounded-md px-3 text-[14px] text-ink hover:bg-tile/70 md:h-9"
                        >
                          Карточка
                        </Link>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(m)}>
                          Изменить
                        </Button>
                        <ViewAsButton member={m} />
                        <HistoryButton table="memberships" rowId={m.membershipId} title={m.fullName} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel title="Приглашения" meta={waiting ? `ждут ответа: ${waiting}` : undefined} bodyClassName="p-0">
        {pending.length === 0 ? (
          <div className="p-5">
            <EmptyState title="Нет открытых приглашений">
              Пригласите сотрудников, поставщиков, подрядчиков и заказчика — каждый увидит только свои
              разделы.
            </EmptyState>
          </div>
        ) : (
          <ul>
            {pending.map((i) => {
              const expired = new Date(i.expiresAt).getTime() <= now;
              return (
                <li
                  key={i.id}
                  className="flex flex-col gap-2 border-t border-line px-5 py-3 first:border-0 sm:flex-row sm:items-center"
                >
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 font-medium text-ink">
                      {i.fullName ?? i.email}
                      {expired ? <Badge tone="warning">Срок вышел</Badge> : null}
                    </div>
                    <div className="text-[13px] text-ink-2">
                      {i.email}, {expired ? 'действовало до' : 'действует до'} {date(i.expiresAt)}
                      {i.invitedBy ? `, пригласил ${i.invitedBy}` : ''}
                    </div>
                    <div
                      className={
                        mailNeedsAttention(i.mail) ? 'text-[12px] text-warning' : 'text-[12px] text-ink-2'
                      }
                    >
                      {mailLine(i.mail)}
                    </div>
                    {canResend(i) ? null : (
                      <div className="text-[12px] text-ink-3">
                        С доступом к деньгам — отправить заново может владелец компании
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {canResend(i) ? (
                      <Button
                        size="sm"
                        variant={expired ? 'primary' : 'secondary'}
                        icon={<Send className="size-4" />}
                        onClick={() => setResending(i)}
                      >
                        Отправить заново
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="danger"
                      loading={revoke.isPending && revoke.variables === i.id}
                      onClick={() => revoke.mutate(i.id)}
                    >
                      Отозвать
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      <ViewAsLogPanel />

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      {resending ? <ResendDialog invite={resending} onClose={() => setResending(null)} /> : null}
      {editing ? (
        <EditMemberDialog key={editing.membershipId} member={editing} onClose={() => setEditing(null)} />
      ) : null}
    </div>
  );
}
